import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { describeError } from '../describeError';
import { detectedTypeWord, formatDate } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { useIsMounted } from '../hooks/useIsMounted';
import { payrollHistoryImportGateway as gateway } from '../index';
import { ISSUE_HELP, columnNamedBy } from '../issueHelp';
import { JURISDICTION_LEVELS, LINE_ROLES, RATE_TYPES, ROLE } from '../mappingRoles';
import {
  blockers, groupTargets, initialRows, requiredItems, rowsFromProfile, toAssignments, withRole, type RowState,
} from '../mapColumnsModel';
import { batchStepPath } from '../routes';
import type { BatchDetail, Issue, Structure, ValidationSummary } from '../types';
import { ErrorSummary } from './basics';

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const truncateHeader = (header: string): string => (header.length > 60 ? header.slice(0, 60) : header);

/** Findings about the mapping itself are fixed here. A missing employee match is fixed on the next step. */
const EMPLOYEE_MATCH_CODES = ['EMPLOYEE_MAPPING_MISSING', 'DUPLICATE_EMPLOYEE_MAPPING'];

function Finding({ issue, inRow }: { issue: Issue; inRow: boolean }) {
  const help = ISSUE_HELP[issue.code];
  const blocking = issue.severity === 'BlockingError';
  const label = blocking ? 'Blocking error' : issue.severity === 'Warning' ? 'Warning' : 'Information';
  // Inside a column's row the "(column '...')" tail is redundant: the finding already sits under that column.
  const message = inRow ? issue.message.replace(/\s*\(column '.*'\)\s*$/, '') : issue.message;
  return (
    <p className={`phi-row-finding${blocking ? '' : ' phi-row-finding--warning'}`}>
      <strong><span aria-hidden="true">{blocking ? '✕ ' : '! '}</span>{label}</strong> <code>{issue.code}</code> {message}
      {help && <span className="phi-plain"> <strong>What to do:</strong> {help.action}</span>}
    </p>
  );
}

interface FormProps {
  batch: BatchDetail;
  structure: Structure;
  announce: (message: string) => void;
  refreshBatch: () => Promise<void>;
  onSheetChange: (sheet: string) => void;
}

function MapColumnsForm({ batch, structure, announce, refreshBatch, onSheetChange }: FormProps) {
  const navigate = useNavigate();
  const mounted = useIsMounted();
  const sheet = structure.sheets.find((s) => s.name === structure.selectedSheet) ?? structure.sheets[0];
  const columns = useMemo(() => sheet?.columns ?? [], [sheet]);
  const groups = useMemo(() => groupTargets(structure.targets), [structure.targets]);
  const labelOf = useCallback((role: string) => structure.targets.find((t) => t.role === role)?.label ?? role, [structure.targets]);
  const earningHeaders = (rows: RowState[]) => columns.filter((_, i) => rows[i]!.role === ROLE.Earning).map((c) => c.header);

  const [rows, setRows] = useState<RowState[]>(() => initialRows(structure, columns));
  const [usedSaved, setUsedSaved] = useState(false);
  const [saveProfile, setSaveProfile] = useState(false);
  const [profileName, setProfileName] = useState(`${structure.providerName} mapping`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [findings, setFindings] = useState<Issue[] | null>(null);

  const items = requiredItems(rows);
  const stopping = blockers(rows, columns, labelOf);
  const notChosen = rows.filter((r) => r.role === '').length;
  const suggestedCount = rows.filter((r) => r.origin === 'suggestion' && r.role !== '').length;

  const update = (index: number, patch: Partial<RowState>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch, origin: 'person', reason: '' } : r)));

  const chooseRole = (index: number, role: string) => {
    const column = columns[index]!;
    const suggestion = structure.suggestions.find((s) => s.sourceColumn.trim().toLowerCase() === column.header.trim().toLowerCase());
    setRows((prev) => prev.map((r, i) => (i === index ? withRole(r, role, suggestion) : r)));
  };

  const useSaved = () => {
    if (!structure.savedProfile) return;
    setRows(rowsFromProfile(structure.savedProfile.mapping, columns));
    setUsedSaved(true);
    announce('The saved column mapping was filled in. Review it and confirm. Nothing is saved until you confirm.');
  };

  const confirm = async () => {
    if (stopping.length > 0 || !sheet) return;
    setSaving(true);
    setError(null);
    let summary: ValidationSummary;
    try {
      summary = await gateway.saveColumnMapping(batch.id, {
        mapping: {
          version: structure.mappingVersion, providerKey: structure.provider, sheetName: structure.selectedSheet,
          columns: toAssignments(rows, columns),
        },
        saveProfile,
        ...(saveProfile ? { profileName: profileName.trim() || `${structure.providerName} mapping` } : {}),
      });
    } catch (err) {
      if (mounted.current) {
        setError(describeError(err, 'Could not save the column mapping.'));
        setSaving(false);
      }
      return;
    }
    if (!mounted.current) return;
    try {
      await refreshBatch();
    } catch {
      if (mounted.current) setError('Your mapping was saved, but the latest status could not be reloaded. Refresh the page to see it.');
    }
    if (!mounted.current) return;
    const mappingProblems = summary.issues.filter((i) => i.severity === 'BlockingError' && !EMPLOYEE_MATCH_CODES.includes(i.code));
    if (mappingProblems.length === 0) {
      announce(
        summary.status === 'ReadyForReview'
          ? 'Column mapping confirmed. The file is ready for review. Next, match your employees.'
          : 'Column mapping confirmed. Next, match your employees.',
      );
      navigate(batchStepPath(batch.id, 'employees'));
      return;
    }
    setFindings(summary.issues);
    setSaving(false);
    announce(`Your mapping was saved, but ${mappingProblems.length} ${mappingProblems.length === 1 ? 'problem needs' : 'problems need'} your attention. Review the findings and change the mapping.`);
  };

  const byColumn = new Map<string, Issue[]>();
  const general: Issue[] = [];
  for (const issue of findings ?? []) {
    if (issue.severity === 'Information') continue;
    const named = columnNamedBy(issue.message);
    const column = named === null ? undefined : columns.find((c) => truncateHeader(c.header.trim()) === named);
    if (column) byColumn.set(column.header, [...(byColumn.get(column.header) ?? []), issue]);
    else general.push(issue);
  }
  const problemCount = (findings ?? []).filter((i) => i.severity === 'BlockingError' && !EMPLOYEE_MATCH_CODES.includes(i.code)).length;

  if (!sheet) return <p>No columns were found in this file.</p>;

  return (
    <div>
      <div className="phi-notice" aria-label="How column matching works" role="group">
        <p><strong>EPN suggests, you confirm.</strong> Nothing is saved or imported until you press Confirm column mapping.</p>
        <p className="muted">
          Dates are read as month/day/year (for example 9/25/2025). Only each column&apos;s heading and the kind of data it holds are shown
          here – never the values in your file.
        </p>
      </div>

      {error && <ErrorSummary message={error} />}
      {findings && problemCount > 0 && (
        <ErrorSummary message={`Your mapping was saved, but ${problemCount} ${problemCount === 1 ? 'problem needs' : 'problems need'} your attention. Review the findings below and change the mapping.`} />
      )}
      {findings && general.length > 0 && (
        <section aria-labelledby="phi-map-general-heading">
          <h3 id="phi-map-general-heading">Findings about your mapping</h3>
          {general.map((issue, i) => <Finding key={`${issue.code}-${i}`} issue={issue} inRow={false} />)}
        </section>
      )}

      {structure.savedProfile && (
        <div className="phi-notice" role="region" aria-label="Saved column mapping">
          <p>
            We found a column mapping you saved on {formatDate(structure.savedProfile.updatedAtUtc.slice(0, 10))}. Review it and confirm.
          </p>
          <p className="muted">Saved as &ldquo;{structure.savedProfile.name}&rdquo;. It only fills in the form; you still confirm it.</p>
          <Button type="button" className="phi-btn-secondary" onClick={useSaved} disabled={saving}>Use saved mapping</Button>
          {usedSaved && <p className="muted">The saved mapping is filled in below. Nothing is saved until you confirm.</p>}
        </div>
      )}

      {structure.sheets.length > 1 && (
        <div className="phi-field">
          <label htmlFor="phi-map-sheet">Sheet</label>
          <select id="phi-map-sheet" value={structure.selectedSheet} onChange={(e) => onSheetChange(e.target.value)}>
            {structure.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>
      )}

      <section aria-labelledby="phi-required-heading">
        <h3 id="phi-required-heading">Required</h3>
        <p>EPN cannot read your file without these. Choose a column for each one.</p>
        <ul className="phi-checklist" aria-label="Required columns" aria-live="polite">
          {items.map((i) => (
            <li key={i.key} className={i.done ? 'phi-check--ok' : 'phi-check--missing'}>
              <span className="phi-check-mark" aria-hidden="true">{i.done ? '✓' : '✗'}</span>
              <span>{i.label}<strong>{i.done ? ' – chosen' : ' – still needed'}</strong></span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="phi-columns-heading">
        <h3 id="phi-columns-heading">Your columns</h3>
        <p className="muted">
          {sheet.columns.length} columns and {sheet.rowCount} rows found.
          {suggestedCount > 0 && ` ${suggestedCount} columns are marked “Suggested – please confirm”: they are only EPN's guess from the heading.`}
        </p>
        <div className="table-scroll">
          <table className="phi-map-table">
            <caption>Say what each column in your file means</caption>
            <thead>
              <tr>
                <th scope="col">Column heading</th>
                <th scope="col">Holds</th>
                <th scope="col">What this column means</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column, i) => {
                const row = rows[i]!;
                const own = byColumn.get(column.header) ?? [];
                const labelId = `phi-map-${i}`;
                const roleSelectId = `${labelId}-role`;
                const duplicate = row.role !== '' && columns.some((c, j) => j !== i && rows[j]!.role === row.role && structure.targets.find((t) => t.role === row.role)?.singleUse);
                return (
                  <tr key={column.index}>
                    <th scope="row" id={labelId}>{column.header || '(no heading)'}</th>
                    <td>{capitalize(detectedTypeWord(column.detectedType))}</td>
                    <td>
                      <select
                        id={roleSelectId}
                        className="phi-select phi-map-select"
                        aria-label={`What “${column.header}” means`}
                        value={row.role}
                        onChange={(e) => chooseRole(i, e.target.value)}
                      >
                        <option value="">Choose what this column means…</option>
                        {row.role === ROLE.DeductionNeedsClassification && (
                          <option value={ROLE.DeductionNeedsClassification}>Deduction – choose before or after tax (required)</option>
                        )}
                        {groups.map((g) => (
                          <optgroup key={g.group} label={g.group}>
                            {g.targets.map((t) => <option key={t.role} value={t.role}>{t.label}</option>)}
                          </optgroup>
                        ))}
                      </select>
                      {row.role !== '' && <p className="phi-help">{structure.targets.find((t) => t.role === row.role)?.help}</p>}

                      {row.origin === 'suggestion' && row.role !== '' && (
                        <>
                          <span className="phi-suggested"><span aria-hidden="true">✎</span>Suggested – please confirm</span>
                          {row.reason && <span className="phi-suggested-reason">{row.reason}</span>}
                        </>
                      )}
                      {row.origin === 'saved' && row.role !== '' && (
                        <span className="phi-suggested"><span aria-hidden="true">✎</span>From your saved mapping – please confirm</span>
                      )}

                      {row.role === ROLE.DeductionNeedsClassification && (
                        <div>
                          <p className="phi-row-error">
                            <span aria-hidden="true">✗ </span>EPN does not guess this. Choose whether this deduction is taken before tax or after tax.
                          </p>
                          <div className="phi-actions">
                            <button type="button" className="phi-btn-secondary" aria-label={`Mark “${column.header}” as taken before tax`} onClick={() => chooseRole(i, ROLE.PreTaxDeduction)}>
                              Taken before tax
                            </button>
                            <button type="button" className="phi-btn-secondary" aria-label={`Mark “${column.header}” as taken after tax`} onClick={() => chooseRole(i, ROLE.PostTaxDeduction)}>
                              Taken after tax
                            </button>
                          </div>
                        </div>
                      )}

                      {duplicate && (
                        <p className="phi-row-error"><span aria-hidden="true">✗ </span>Another column already has this meaning. Only one column can.</p>
                      )}

                      <div className="phi-map-extra">
                        {row.role === ROLE.Earning && (
                          <>
                            <div className="phi-field">
                              <label htmlFor={`${labelId}-rate`}>Pay type</label>
                              <select id={`${labelId}-rate`} value={row.rateType} onChange={(e) => update(i, { rateType: e.target.value })}>
                                {RATE_TYPES.map((r) => <option key={r} value={r}>{r === 'DoubleTime' ? 'Double time' : r}</option>)}
                              </select>
                            </div>
                            <div className="phi-checkbox">
                              <input id={`${labelId}-taxable`} type="checkbox" checked={row.taxable} onChange={(e) => update(i, { taxable: e.target.checked })} />
                              <label htmlFor={`${labelId}-taxable`}>Taxable wages</label>
                            </div>
                          </>
                        )}
                        {(row.role === ROLE.EmployeeTax || row.role === ROLE.EmployerTax) && (
                          <>
                            <div className="phi-field">
                              <label htmlFor={`${labelId}-level`}>Tax level</label>
                              <select id={`${labelId}-level`} value={row.jurisdictionLevel} onChange={(e) => update(i, { jurisdictionLevel: e.target.value })}>
                                {JURISDICTION_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                              </select>
                            </div>
                            {row.jurisdictionLevel === 'State' && (
                              <div className="phi-field">
                                <label htmlFor={`${labelId}-state`}>State code (2 letters)</label>
                                <input
                                  id={`${labelId}-state`} type="text" maxLength={2} autoComplete="off" value={row.stateCode}
                                  onChange={(e) => update(i, { stateCode: e.target.value.toUpperCase() })}
                                />
                              </div>
                            )}
                          </>
                        )}
                        {row.role === ROLE.Hours && (
                          <div className="phi-field">
                            <label htmlFor={`${labelId}-link`}>Hours belong to this earning</label>
                            <select id={`${labelId}-link`} value={row.earningLink} onChange={(e) => update(i, { earningLink: e.target.value })}>
                              <option value="">Choose an earning column…</option>
                              {earningHeaders(rows).map((h) => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                        )}
                        {LINE_ROLES.has(row.role) && (
                          <div className="phi-field">
                            <label htmlFor={`${labelId}-label`}>Label (optional)</label>
                            <input id={`${labelId}-label`} type="text" maxLength={80} value={row.label} onChange={(e) => update(i, { label: e.target.value })} />
                          </div>
                        )}
                      </div>

                      {own.map((issue, k) => <Finding key={`${issue.code}-${k}`} issue={issue} inRow />)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="phi-save-heading">
        <h3 id="phi-save-heading">Save for next time</h3>
        <div className="phi-checkbox">
          <input id="phi-save-profile" type="checkbox" checked={saveProfile} onChange={(e) => setSaveProfile(e.target.checked)} />
          <label htmlFor="phi-save-profile">Save this mapping for next time</label>
        </div>
        <div className="phi-field">
          <label htmlFor="phi-profile-name">Name for this saved mapping</label>
          <input id="phi-profile-name" type="text" maxLength={80} value={profileName} disabled={!saveProfile} onChange={(e) => setProfileName(e.target.value)} />
        </div>
        <p className="muted">A saved mapping only pre-fills this form for a file with the same columns. You still confirm it every time.</p>
      </section>

      {notChosen > 0 && (
        <p className="muted">
          {notChosen} {notChosen === 1 ? 'column has' : 'columns have'} no meaning chosen and will not be imported.
        </p>
      )}
      {stopping.length > 0 && (
        <div className="phi-notice" id="phi-confirm-blockers">
          <p><strong>Before you can confirm:</strong></p>
          <ul>{stopping.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      <p className="muted">By confirming, you are saying that each column means what is chosen above, including the ones EPN suggested.</p>

      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, 'found')} className="phi-btn-secondary">Back</Link>
        <Button
          type="button"
          disabled={stopping.length > 0}
          {...(stopping.length > 0 ? { 'aria-describedby': 'phi-confirm-blockers' } : {})}
          loading={saving}
          loadingLabel="Confirming…"
          onClick={() => void confirm()}
        >
          Confirm column mapping
        </Button>
      </div>
    </div>
  );
}

interface Props {
  batch: BatchDetail;
  announce: (message: string) => void;
  refreshBatch: () => Promise<void>;
}

/** Step 4 (guided mapping only): the person says what each column means. */
export function MapColumnsStep({ batch, announce, refreshBatch }: Props) {
  const [sheet, setSheet] = useState<string | undefined>(undefined);
  const load = useCallback((signal: AbortSignal) => gateway.getStructure(batch.id, sheet, signal), [batch.id, sheet]);
  const { loading, data, error, reload } = useAsyncData(load, { keepPrevious: true });

  if (loading && !data) return <p className="muted" role="status">Reading your file&apos;s columns…</p>;
  if (error !== undefined || !data) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not read the columns of your file.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }
  return (
    <MapColumnsForm
      key={`${data.fingerprint}-${data.selectedSheet}`}
      batch={batch}
      structure={data}
      announce={announce}
      refreshBatch={refreshBatch}
      onSheetChange={setSheet}
    />
  );
}
